import React, { useEffect, useMemo, useState } from 'react';
import {
  FieldDateAndTimePicker,
  FieldDatepicker,
  FieldInput,
  FormCol,
  FormCol6,
  FormRow,
  useIsFieldRequiredHook,
} from 'motion-components';
import { useField, useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  FieldSelectWasteOriginType,
  FieldSelectWasteType,
  FieldSelectWasteDestinationType,
  FileRepoImage,
  FieldSelectCollector,
  FormFieldCollectionPointDlg,
  FormFieldCollectionDlg,
  FieldSelectVehicle,
  NumberingSeries,
  AlarmTypeValues,
} from 'shared-pages';
import { useApolloClient, useMutation } from '@apollo/client';

import { getWasteReceptionPointById, getWasteReceptionPoint, getMaterialById } from '../graphql';
import {
  IdocWasteOriginTypes,
  FieldSelectRegion,
  FieldSelectSubregion,
  FieldSelectDriver,
  FieldSelectWasteReceptionPoint,
} from 'shared-pages';
import { IDocExtrasTable } from './idocextras-table';
import { ColButtonNew } from './col-button-new';
import {
  promiseModalVehicleInput,
  promiseModalDriverInput,
  promiseModalCollectionPointInput,
  FieldSelectSeries,
} from '../../../components';
import {
  CompanyVatTitle,
  isAfisCyprus,
  isMedicycle,
  isRebattery,
  isRebatteryLiion,
} from 'shared-pages/common/constants';
import { gqlAddAlarm } from 'shared-pages/pages/alarms/graphql';
import { isCancelled, isIdocMulti } from 'shared-pages/common/form-helpfunc';

import { FieldSelectCpCategory } from 'shared-pages/components/field-select/fs-cpCategory';
import { FieldSelectDriverByAdr } from 'shared-pages/components/field-select/fs-driversByAdr';
import { hasDateStorage, hasReceptionNetWeight } from './idoc-reception-validation';
import { useIdocFormHandlers } from '../hooks/useIdocFormHandlers';
import { useTriggerMailService } from 'shared-pages/hooks/trigger-mail-service';
import { generatePath, Link } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import { FormatDateTime } from 'shared-pages/common/date-helpfunc';

/*
    Form Fields
*/
export const IdocFormFields = ({
  isNew,
  updateRecord,
  refetch,
  data,
  initialNewRecData,
  validationSchema = undefined,
}) => {
  //
  const { t } = useTranslation();
  const client = useApolloClient();
  const isFieldRequired = useIsFieldRequiredHook(validationSchema);
  const { triggerMailService } = useTriggerMailService();
  const [vehicleKey, setVehicleKey] = useState(1);
  const [driverKey, setDriverKey] = useState(1);
  const { values, initialValues, setFieldValue, errors, isValid, touched } =
    useFormikContext<any>();

  const {
    updateCollectionPointFields,
    onCollectorIdChange,
    updateDriverFields,
    updateVehicleFields,
    onWastereceptionpointChange,
    onCollectionChange,
  } = useIdocFormHandlers(setFieldValue);

  const [addAlarm] = useMutation(gqlAddAlarm);

  useEffect(() => {
    if (!isNew || !initialNewRecData) return;

    const initializeForm = async () => {
      // Safe inside Formik context
      setFieldValue('wasteOriginTypeId', initialNewRecData?.wasteOriginTypeId);

      if (initialNewRecData?.collectionPointId) {
        await updateCollectionPointFields(initialNewRecData?.collectionPointId);
      }

      if (initialNewRecData?.collectorId) {
        await onCollectorIdChange({ value: initialNewRecData?.collectorId });
      }

      if (initialNewRecData?.driverId) {
        await updateDriverFields(initialNewRecData?.driverId, () =>
          setDriverKey?.((prev) => prev + 1),
        );
      }

      if (initialNewRecData?.vehicleId) {
        await updateVehicleFields(initialNewRecData.vehicleId, () =>
          setVehicleKey?.((prev) => prev + 1),
        );
      }

      if (initialNewRecData?.wastereceptionpointId) {
        await onWastereceptionpointChange({ value: initialNewRecData?.wastereceptionpointId });
      }
      //collectionId
      if (initialNewRecData?.collectionId) {
        setFieldValue('collectionId', initialNewRecData?.collectionId);
        setFieldValue('collectionCode', initialNewRecData?.collectionCode);
      }
    };

    initializeForm();
  }, [isNew, initialNewRecData]);
  const { appStore } = useRootStore();
  const canEditSection1 =
    appStore.isPlatformOwner ||
    appStore.isCollector ||
    appStore.isCollectorDriver ||
    (appStore.isWasteReceptionPoint && (isNew || data?.wasteOriginTypeId === 2));
  const canEditSection2 =
    appStore.isPlatformOwner ||
    appStore.isCollector ||
    appStore.isCollectorDriver ||
    (appStore.isWasteReceptionPoint && (isNew || data?.wasteOriginTypeId === 2));
  const canEditSection3a =
    appStore.isPlatformOwner ||
    appStore.isWasteReceptionPoint ||
    appStore.isCollector ||
    appStore.isCollectorDriver;
  const canEditSection3b = appStore.isPlatformOwner || appStore.isWasteReceptionPoint;

  //
  // console.log('IdocFormFields', values);
  const isDataEditable = () => {
    return false;
  };
  const TextField = ({ label, name }) => {
    return <FieldInput disabled={!isDataEditable()} as={FormCol} label={label} name={name} />;
  };

  const DateTimeField = ({ label, name, disabled, required = false, as }) => {
    return (
      <FieldDatepicker
        required={required}
        disabled={disabled}
        as={as}
        label={label}
        name={name}
        dateFormat={'dd/MM/yyyy HH:mm'}
      />
    );
  };

  const DateAndTimeField = ({ label, labelTime, name, disabled, required = false, as }) => {
    return (
      <FieldDateAndTimePicker
        disabled={disabled}
        required={required}
        as={as}
        label={label}
        labelTime={labelTime}
        name={name}
        dateFormat={'dd/MM/yyyy'}
      />
    );
  };

  /** Υπολογισμός Καθαρού Βάρους
      Υπολόγισε το Καθαρό Βάρος αν αλλάξει το Μικτό ή το Απόβαρο
  */
  useEffect(() => {
    if (isRebattery) return;

    const gw = values.grossWeight;
    const tw = values.tareWeight;

    const initGw = initialValues?.grossWeight;
    const initTw = initialValues?.tareWeight;

    const grossChanged = gw !== initGw;
    const tareChanged = tw !== initTw;

    // Στο φόρτωμα/reinitialize μην κάνεις override
    if (!grossChanged && !tareChanged) return;

    if (gw === null || gw === undefined || gw === '') return;

    const weight = tw === null || tw === undefined || tw === '' ? gw : gw - tw;

    setFieldValue('netWeight', weight);
    setFieldValue('receptionNetWeight', weight);
  }, [
    isRebattery,
    values.grossWeight,
    values.tareWeight,
    initialValues?.grossWeight,
    initialValues?.tareWeight,
    setFieldValue,
  ]);

  /*
     Καθορισμός Σημείου Συλλογής
  */
  async function onCollectionPointChange(valueId) {
    if (valueId) {
      await updateCollectionPointFields(parseInt(valueId));
    }
  }

  /*
     Καθορισμός Συλλογής
  */

  /** Καθορισμός Νέου Σημείου Συλλογής */
  const [collpointKey, setCollpointKey] = useState(1);
  async function handleNewCollectionpoint(props) {
    const id = await promiseModalCollectionPointInput({
      title: t('Δήλωση Νέου Σημείου Συλλογής'),
    });
    if (id) {
      console.log('New Collection Point', id);
      await updateCollectionPointFields(id);
    }
  }

  /** Καθορισμός Οδηγού του Συλλέκτη */
  async function onDriverChange(e) {
    console.log('$$$ driverChange', e, 'values.driverId=', values.driverId, values.driverAdr);
    if (e.value === null) return;
    if (values.driverId === e.value) return;

    const id = parseInt(e.value);

    await updateDriverFields(id, () => setDriverKey((k) => k + 1));
  }

  /** Καθορισμός Νέου Οδηγού Συλλέκτη */
  async function handleNewDriver(props) {
    console.log('$$$ handleNewDriver', values);
    const id = await promiseModalDriverInput({
      title: 'Δήλωση Νέου Οδηγού',
      collectorId: values.collectorId,
    });
    if (id) {
      console.log('New Driver', id);
      await updateDriverFields(id, () => setDriverKey((k) => k + 1));
      await addAlarm({
        variables: {
          data: {
            alarmDate: new Date(),
            driverId: id,
            alarmTypeId: AlarmTypeValues.CollectionDriver,
            collectorId: values.collectorId,
            link: `/driver/${id}`,
          },
        },
      }).then(() => {
        // Στείλε τα mail που έβαλε το WebApi στο MailRepo
        try {
          triggerMailService();
        } catch (error) {
          console.log('triggerMailService', error);
        }
      });
    }
  }

  /**  Update Driver Fields From DB */

  /** Καθορισμός Νέου Οχήματος Συλλέκτη */
  async function handleNewVehicle(props) {
    const id = await promiseModalVehicleInput({
      title: t('Δήλωση Νέου Οχήματος'),
      collectorId: values.collectorId,
    });
    if (id) {
      console.log('New Vehicle', id);
      await updateVehicleFields(id, () => setVehicleKey((k) => k + 1));
      await addAlarm({
        variables: {
          data: {
            alarmDate: new Date(),
            vehicleId: id,
            alarmTypeId: AlarmTypeValues.CollectionVehicle,
            collectorId: values.collectorId,
            link: `/vehicle/${id}`,
          },
        },
      }).then(() => {
        triggerMailService();
      });
    }
  }

  /** Καθορισμός Οχήματος του Συλλέκτη */
  async function onVehicleChange(e) {
    if (e.value === null) return;
    if (values.vehicleId === e.value) return;
    const id = parseInt(e.value);

    await updateVehicleFields(id, () => setVehicleKey((k) => k + 1));
  }

  /**  Update Driver Fields From DB */

  /*
     Τύπος Αποβλήτων - Απόβλητο
  */
  async function onWastetypeChange(e) {
    if (e.value === null) return;
    if (values.materialId === e.value) return;
    const id = parseInt(e.value);

    const rec = await getMaterialById(client, id);
    if (!rec || rec === null) return;
    setFieldValue('ekaCode', rec?.defEkaCode);
    setFieldValue('unClass', rec?.defUnClass);
  }

  /*
     Λήψη απο Δευτερεύουσα Αποθήκευση
  */
  async function onFromWastereceptionpointChange(e) {
    if (e.value === null) return;
    if (values.fromWastereceptionpointId === e.value) return;
    const id = parseInt(e.value);

    const rec = await getWasteReceptionPointById(client, id);

    if (!rec || rec === null) return;
    setFieldValue('collectionPointId', null);
    setFieldValue('fromWastereceptionpointId', id);
    setFieldValue('collectionPointName', rec.name);
    setFieldValue('colpointAddressLine1', rec.addressLine1);
    setFieldValue('colpointAddressLine2', rec.addressLine2);
    setFieldValue('colpointAddressArea', rec.addressArea);
    setFieldValue('colpointAddressCity', rec.addressCity);
    setFieldValue('colpointPostalCode', rec.postalCode);
    setFieldValue('colpointPhoneNumber', rec.phone);
    !isAfisCyprus && setFieldValue('colpointVatNumber', rec.companyVat);
    !isAfisCyprus && setFieldValue('colpointTaxOffice', rec.companyTaxOffice);
    setFieldValue('regionId', rec.regionId);
    setFieldValue('subregionId', rec.subregionId);
    setFieldValue('rbGeoAreaId', null);
    setFieldValue('colpointContactPerson', rec.receptionPersonName);
    setFieldValue('colpointCategoryId', rec.categoryId);
    setFieldValue('colpointImaCode', rec.imaCode);
  }
  async function onFinaldestinationpointChange(e) {
    if (e.value === null) return;
    if (values.finaldestinationpointId === e.value) return;

    const id = parseInt(e.value);
    const rec = await getWasteReceptionPointById(client, id);
    if (!rec) return;

    // FK
    setFieldValue('finaldestinationpointId', id);

    setFieldValue('finaldestinationpoint.wasteDestinationTypeId', rec.wasteDestinationTypeId);

    // Mirror of Section 3 “company” fields, but for finaldestination*
    setFieldValue('finaldestinationCompanyName', rec.name);
    setFieldValue('finaldestinationCompanyAddress', rec.addressLine1);
    setFieldValue('finaldestinationCompanyPhoneNumber', rec.phone);
    !isAfisCyprus && setFieldValue('finaldestinationCompanyVat', rec.companyVat);
    !isAfisCyprus && setFieldValue('finaldestinationCompanyTaxOffice', rec.companyTaxOffice);
    setFieldValue('finaldestinationCompanyImaCode', rec.imaCode ?? rec.companyImaCode ?? '');
    setFieldValue('finaldestinationCompanyRegionId', rec.regionId ?? null);
    setFieldValue('finaldestinationCompanySubregionId', rec.subregionId ?? null);
    setFieldValue('finaldestinationCompanyEmail', rec.email ?? rec.companyEmail ?? '');

    // Helpful extra
    setFieldValue('finalDestinationPersonName', rec.receptionPersonName ?? '');
  }
  /*
      Προέλευση Αποβλήτων
  */
  async function onWasteOriginTypeIdChange(e) {
    // Βάλε Δευτερεύουσα Αποθήκη αν έχει μόνο μία
    if (e.value === null) return;
    if (values.wasteOriginTypeId === e.value) return;
    const id = parseInt(e.value);

    if (id === IdocWasteOriginTypes.SecondaryStorage) {
      const collectorId = values.collectorId;
      const rec = await getWasteReceptionPoint(client, { collectorId });
      if (rec?.recordCount === 1) {
        const fromWastereceptionpointId = rec.data[0].id;
        setFieldValue('fromWastereceptionpointId', fromWastereceptionpointId);
      }
    }
  }

  const showCollection = useMemo(() => {
    // Εμφάνιση Συλλογής Αποβλήτων
    // πάντα
    // για ReBattery μόνο αν έχει τιμή το collectionId
    return isRebattery ? values?.collectionId !== null : true;
  }, [values?.collectionId, isRebattery]);

  const showITransLine = useMemo(() => {
    // Εμφάνιση Εσωτερικής Διακίνησης Αποβλήτων
    // πάντα αν έχει τιμή το iTransLineId
    return values?.iTransLineId != null;
  }, [values?.iTransLineId]);

  const iTranId = values?.iTransLine?.internalTransaction?.id ?? values?.iTransLine?.itransId;

  const transferredITrans = values?.transferredWithITrans;
  const transferredITransOriginName =
    transferredITrans?.wasteOriginTypeId === IdocWasteOriginTypes.PrimaryStorage
      ? transferredITrans?.fromCollectionpoint?.name
      : transferredITrans?.fromWastereceptionpoint?.name;
  /*
     Καθορισμός Συλλέκτη
  */

  /* ==============================================================================================================
     Render
     ============================================================================================================== */
  return (
    <>
      <div>
        <FormRow>
          <FieldSelectSeries
            as={FormCol}
            required={true}
            label="Σειρά Εντύπου Αναγνώρισης"
            name="idocseriesId"
            seriestypeId={NumberingSeries.IDocs}
            disabled={!canEditSection1}
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Κωδικός ΕΑΕΑ."
            name="idocPrefix"
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Αριθμός ΕΑΕΑ."
            name="idocNumber"
            type="number"
          />
          <FormCol>
            {isNew
              ? t('(*) Αφήστε τον Αριθμό κενό για να ενημερωθεί αυτόματα κατά την αποθήκευση')
              : isRebattery
              ? isIdocMulti(t, false) // Σύνθετο Δρομολόγιο
              : ''}
          </FormCol>
        </FormRow>
        {isRebattery && (
          <FormRow>
            <FormCol>{isCancelled(t, true)}</FormCol>
          </FormRow>
        )}
        {/*  1. Προέλευση Αποβλήτων   ---------------------------------------------------------------   */}
        <h5 className="form-group-title">1. {t('Προέλευση Αποβλήτων')}</h5>
        <FormRow>
          <FieldSelectWasteOriginType
            required={!values?.isCancelled}
            disabled={!canEditSection1}
            as={FormCol}
            name="wasteOriginTypeId"
            defaultValues={data}
            onChange={onWasteOriginTypeIdChange}
          />
          <FieldDatepicker
            disabled={!canEditSection1}
            required={true}
            hasMask={true}
            placeholder={'dd/mm/yyyy HH:mm'}
            as={FormCol}
            label="Ημ/νία & Ώρα Παραλαβής"
            showTimeSelect
            dateFormat="d/M/yyyy HH:mm"
            timeFormat="HH:mm"
            timeIntervals={15}
            name="dateCollected"
          />
          {/* <FieldDateAndTimePicker
            disabled={!canEditSection1}
            required={!values?.isCancelled}
            as={FormCol}
            label="Ημ/νία Παραλαβής"
            labelTime="Ώρα Παραλαβής"
            name="dateCollected"
          /> */}
          {/* <DateAndTimeField
            disabled={!canEditSection1}
            required={!values?.isCancelled}
            as={FormCol}
            label="Ημ/νία Παραλαβής"
            labelTime="Ώρα Παραλαβής"
            name="dateCollected"
          /> */}

          {isRebattery ? (
            <FieldSelectSubregion
              required={values?.isCancelled ? false : isFieldRequired('subregionId', values)}
              as={FormCol}
              name={'subregionId'}
            />
          ) : (
            <FieldSelectRegion
              name="regionId"
              required={values?.isCancelled ? false : isFieldRequired('regionId', values)}
              as={FormCol}
              disabled={!canEditSection1}
            />
          )}
          {/* Δείξε τον Δήμο αν υπάρχουν δεδομένα */}
          {/* {data && data.rbGeoAreaId && (
            <FieldSelectRbGeoArea
              // defaultValues={data.rbGeoArea.municipalityOrNomos}
              defaultValues={{ name: 'xala', municipalityOrNomos: '123' }}
              as={FormCol}
              name={'rbGeoAreaId'}
            />
          )} */}
        </FormRow>
        {/* Στην ReBattery η λήψη γίνεται πάντα απο το Σημείο Συλλογής
        και δεν υπάρχει Δευτερεύουσα Αποθήκευση */}
        {isRebattery ? (
          <FormRow>
            {/* Λήψη απο Σημείο Συλλογής */}
            <FormFieldCollectionPointDlg
              as={FormCol6}
              label={t('Επωνυμία')}
              disabled={!canEditSection1}
              onChange={(valueId) => {
                if (!valueId) return;
                const id = parseInt(valueId);
                if (values.collectionPointId === id) return;
                onCollectionPointChange(valueId);
              }}
            />
            <ColButtonNew
              label={t('Νέο Σημείο Παραλαβής')}
              onClick={handleNewCollectionpoint}
              disabled={!canEditSection1}
            />
          </FormRow>
        ) : (
          <FormRow>
            {/* Λήψη απο Σημείο Συλλογής  */}
            {values?.wasteOriginTypeId === IdocWasteOriginTypes.PrimaryStorage && (
              <>
                <FormFieldCollectionPointDlg
                  as={FormCol6}
                  label={t('Επωνυμία')}
                  // key={collpointKey}
                  disabled={!canEditSection1}
                  onChange={onCollectionPointChange}
                />
                <ColButtonNew
                  label={t('Νέο Σημείο Παραλαβής')}
                  onClick={handleNewCollectionpoint}
                  disabled={!canEditSection1}
                />
                {showCollection && (
                  <>
                    {/* Εντολή Συλλογής Αποβλήτων */}
                    <FormFieldCollectionDlg
                      as={FormCol}
                      label={t('Συλλογή')}
                      idFieldName="collectionId"
                      nameFieldName="collectionCode"
                      disabled={!canEditSection1}
                      onChange={onCollectionChange}
                    />
                  </>
                )}
              </>
            )}
            {/* Λήψη απο Δευτερεύουσα Αποθήκευση */}
            {values?.wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage && (
              <FieldSelectWasteReceptionPoint
                required={!values?.isCancelled}
                as={FormCol6}
                disabled={!canEditSection1}
                onChange={onFromWastereceptionpointChange}
                name="fromWastereceptionpointId"
                //collectorId={values?.collectorId}
              />
            )}
            {/* Εσωτερική Διακίνηση Αποβλήτων */}
            {showITransLine && (
              <>
                {/* Εσωτερική Διακίνηση Αποβλήτων */}
                <FieldInput
                  as={FormCol}
                  label={
                    iTranId != null ? (
                      <Link to={generatePath(getRouteByName('ITran'), { id: iTranId })}>
                        {t('Εσωτερική Διακίνηση Αποβλήτων')}
                      </Link>
                    ) : (
                      t('Εσωτερική Διακίνηση Αποβλήτων')
                    )
                  }
                  name="iTransLine.internalTransaction.code"
                  disabled={true}
                />
              </>
            )}
          </FormRow>
        )}

        {/* <FormRow>
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Επωνυμία"
            name="collectionPointName"
          />
          {canEditSection1 && values?.wasteOriginTypeId === IdocWasteOriginTypes.PrimaryStorage && (
            <CollectionPointIdDlg disabled={!canEditSection1} />
          )}

          {canEditSection1 && values?.wasteOriginTypeId === IdocWasteOriginTypes.SecondaryStorage && (
            <FieldSelectWasteReceptionPoint
              as={FormCol}
              disabled={!canEditSection1}
              onChange={onFromWastereceptionpointChange}
              name="fromWastereceptionpointId"
              // collectorId={values?.collectorId}
            />
          )}
        </FormRow> */}
        <FormRow>
          <FieldInput
            name="colpointAddressLine1"
            required={values?.isCancelled ? false : isFieldRequired('colpointAddressLine1', values)}
            disabled={!canEditSection1}
            as={FormCol}
            label="Διεύθυνση"
          />
          {!isAfisCyprus && (
            <FieldInput
              name="colpointVatNumber"
              required={values?.isCancelled ? false : isFieldRequired('colpointVatNumber', values)}
              disabled={!canEditSection1}
              as={FormCol}
              label={CompanyVatTitle}
            />
          )}
          {isRebattery && (
            <FieldInput
              disabled={!canEditSection1}
              as={FormCol}
              label="ΑΜ. ΗΜΑ"
              name="colpointImaCode"
            />
          )}
        </FormRow>
        <FormRow>
          <FieldInput
            name="colpointAddressLine2"
            required={values?.isCancelled ? false : isFieldRequired('colpointAddressLine2', values)}
            disabled={!canEditSection1}
            as={FormCol}
            label="Διεύθυνση (γραμμή 2)"
          />
          {!isAfisCyprus && !isMedicycle && (
            <FieldInput
              required={!values?.isCancelled}
              disabled={!canEditSection1}
              as={FormCol}
              label="Δ.Ο.Υ.."
              name="colpointTaxOffice"
            />
          )}
          {isRebattery && (
            <FieldSelectCpCategory
              required={false}
              disabled={!canEditSection1}
              as={FormCol}
              name="colpointCategoryId"
              defaultValues={data}
            />
          )}
        </FormRow>
        <FormRow>
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Περιοχή"
            name="colpointAddressArea"
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Τ.Κ."
            name="colpointPostalCode"
          />
        </FormRow>
        <FormRow>
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Πόλη"
            name="colpointAddressCity"
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Τηλέφωνο"
            name="colpointPhoneNumber"
          />
        </FormRow>
        <FormRow>
          <FieldSelectWasteType
            required={!values?.isCancelled}
            disabled={!canEditSection1}
            as={FormCol}
            name="materialId"
            defaultValues={data}
            onChange={onWastetypeChange}
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="ΕΚΑ."
            name="ekaCode"
            required={values?.isCancelled ? false : isFieldRequired('ekaCode', values)}
          />
          <FieldInput disabled={!canEditSection1} as={FormCol} label="Κλάση UN" name="unClass" />
        </FormRow>
        <FormRow>
          {/* <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Αριθμός Συσκευασιών"
            name="containersQty"
          /> */}
          <FieldInput
            disabled={!canEditSection1}
            required={!values?.isCancelled}
            as={FormCol}
            label="Μικτό Βάρος Φορτίου (kg)"
            name="grossWeight"
            type="number"
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Απόβαρο Συσκευασίας(-ων) (kg)"
            name="tareWeight"
            type="number"
          />
          {!isRebattery && (
            <FieldInput
              disabled={true}
              as={FormCol}
              label="Καθαρό Βάρος Φορτίου (σε κιλά)"
              name="netWeight"
              type="number"
            />
          )}
        </FormRow>
        <FormRow>
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Υπεύθυνος"
            name="colpointContactPerson"
          />
          <FileRepoImage fileId={data?.originSignFileId} />
        </FormRow>

        {/* ---- Εξτρα Μπαταρίες -----*/}
        {((isNew && initialNewRecData) || (!isNew && data)) && (
          <div className="mb-5">
            <IDocExtrasTable
              data={isNew ? initialNewRecData.idocExtras : data?.idocExtras}
              idocId={data?.id}
              canEdit={canEditSection1}
            />
          </div>
        )}

        {/*  2. Συλλέκτης / Μεταφορέας   ---------------------------------------------------------------   */}

        <h5 className="form-group-title">2. {t('Συλλέκτης / Μεταφορέας')}</h5>
        <FormRow>
          <FieldSelectCollector
            required={!values?.isCancelled}
            as={FormCol}
            name="collectorId"
            label={t('Συλλέκτης')}
            onChange={(e) => {
              if (e.value === null) return;
              if (values.collectorId === e.value) return;
              onCollectorIdChange(e);
            }}
            defaultValues={values}
            disabled={!canEditSection2}
          />
          {/* <TextField label="Επωνυμία" name="collectorCompanyName" /> */}
          {/* <DateTimeField
            disabled={!canEditSection2}
            as={FormCol}
            label="Ημ/νία Φόρτωσης"
            name="dateVehLoading"
          /> */}
          {/* <TextField label="Διεύθυνση" name="collectorCompanyAddress" /> */}
          <FieldInput
            required={
              values?.isCancelled ? false : isFieldRequired('collectorCompanyAddress', values)
            }
            disabled={!canEditSection2}
            as={FormCol}
            label="Διεύθυνση"
            name="collectorCompanyAddress"
          />
        </FormRow>
        <FormRow>
          <FieldInput
            required={
              values?.isCancelled ? false : isFieldRequired('collectorCompanyPhoneNumber', values)
            }
            disabled={!canEditSection2}
            as={FormCol}
            label="Τηλέφωνο"
            name="collectorCompanyPhoneNumber"
          />
          <TextField label="ΑΜ. ΗΜΑ" name="collectorCompanyImaCode" />

          <TextField label={CompanyVatTitle} name="collectorCompanyVat" />
          {!isAfisCyprus && !isMedicycle && (
            <TextField label="Δ.Ο.Υ." name="collectorCompanyTaxOffice" />
          )}
        </FormRow>
        <FormRow>
          {isRebattery ? (
            <>
              <FieldInput
                required={!values?.isCancelled}
                disabled={!canEditSection2}
                as={FormCol}
                label="Οδηγός"
                name="driverFullName"
              />
              <FieldSelectDriverByAdr
                required={values?.isCancelled ? false : isFieldRequired('driverId', values)}
                key={driverKey}
                disabled={!canEditSection2}
                as={FormCol}
                collectorId={values?.collectorId ?? undefined}
                name="driverId"
                defaultValues={data}
                onChange={onDriverChange}
              />
            </>
          ) : (
            <>
              <FieldSelectDriver
                required={values?.isCancelled ? false : isFieldRequired('driverId', values)}
                key={driverKey}
                disabled={!canEditSection2}
                as={FormCol}
                collectorId={values?.collectorId ?? undefined}
                name="driverId"
                defaultValues={data}
                onChange={onDriverChange}
              />
              <FieldInput
                required={values?.isCancelled ? false : isFieldRequired('adrNumber', values)}
                disabled={!canEditSection1}
                as={FormCol}
                label="Αρ. Αδείας Οδηγού ADR"
                name="adrNumber"
              />
            </>
          )}

          <FileRepoImage fileId={values?.driverSignFileId} />

          <ColButtonNew
            label={t('Νέος Οδηγός')}
            onClick={handleNewDriver}
            disabled={!canEditSection2}
          />
        </FormRow>
        <FormRow>
          <FieldSelectVehicle
            required={!values?.isCancelled}
            disabled={!canEditSection2}
            key={vehicleKey}
            as={FormCol}
            collectorId={values?.collectorId ?? undefined}
            name="vehicleId"
            defaultValues={data}
            onChange={onVehicleChange}
            label="Αρ. Κυκλ. Οχήματος"
          />
          <ColButtonNew
            label={t('Νέο Όχημα')}
            onClick={handleNewVehicle}
            disabled={!canEditSection2}
          />
        </FormRow>

        <FormRow>
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Διαδρομή (άλλη από την πλέον βέλτιστη)"
            name="altRouteDescription"
          />
        </FormRow>

        {/*  --------------------------------------------------------------------------------------   */}
        {/*  3. Παράδοση αποβλήτων   ----------------------------------------------------------------   */}
        {/*  --------------------------------------------------------------------------------------   */}

        <h5 className="form-group-title">3. {t('Παράδοση αποβλήτων')}</h5>
        <FormRow>
          <FieldSelectWasteReceptionPoint
            required={!values?.isCancelled}
            disabled={!canEditSection3a}
            as={FormCol}
            name="wastereceptionpointId"
            defaultValues={data}
            onChange={(e) => {
              if (e.value === null) return;
              if (values.wastereceptionpointId === e.value) return;
              onWastereceptionpointChange(e);
            }}
            // onlySecondaryStorage={
            //   values?.wasteDestinationTypeId === IdocWasteDestinationTypes.SecondaryStorage
            //     ? true
            //     : undefined
            // }
            // onlyRecyclers={
            //   values?.wasteDestinationTypeId === IdocWasteDestinationTypes.Recycling
            //     ? true
            //     : undefined
            // }
          />
          <FieldDatepicker
            disabled={!canEditSection3b}
            required={
              values?.isCancelled ? false : hasReceptionNetWeight(values?.receptionNetWeight)
            }
            as={FormCol}
            label="Ημ/νία & Ώρα Παράδοσης"
            hasMask={true}
            placeholder={'dd/mm/yyyy HH:mm'}
            showTimeSelect
            dateFormat="d/M/yyyy HH:mm"
            timeFormat="HH:mm"
            timeIntervals={15}
            name="dateStorage"
          />
          {/* <DateAndTimeField
            required={!values?.isCancelled}
            disabled={!canEditSection3b}
            as={FormCol}
            label="Ημ/νία Παράδοσης"
            labelTime={'Ώρα Παράδοσης'}
            name="dateStorage"
          /> */}
        </FormRow>
        <FormRow>
          <FieldSelectWasteDestinationType
            name="wasteDestinationTypeId"
            required={!values?.isCancelled}
            disabled={true}
            as={FormCol}
            label={t('Κατηγορία Τελικού Αποδέκτη')}
            defaultValues={data}
          />
          <TextField label="Διεύθυνση" name="wastereceptionpointCompanyAddress" />

          {/* <FieldInput
            disabled={!canEditSection3a}
            as={FormCol}
            label="Επωνυμία"
            name="wastereceptionpointCompanyName"
            /> */}
          <TextField label="ΑΜ. ΗΜΑ" name="wastereceptionpointCompanyImaCode" />
        </FormRow>
        <FormRow>
          <FieldInput
            disabled={!canEditSection3a}
            as={FormCol}
            label="Τηλέφωνο"
            name="wastereceptionpointCompanyPhoneNumber"
          />
          {isRebattery ? (
            <FieldSelectSubregion
              required={!values?.isCancelled}
              as={FormCol}
              name={'wastereceptionpointCompanySubregionId'}
            />
          ) : (
            <FieldSelectRegion
              name={'wastereceptionpointCompanyRegionId'}
              required={
                values?.isCancelled
                  ? false
                  : isFieldRequired('wastereceptionpointCompanyRegionId', values)
              }
              as={FormCol}
              disabled={!canEditSection3a}
            />
          )}
          <TextField label={CompanyVatTitle} name="wastereceptionpointCompanyVat" />
          {!isAfisCyprus && !isMedicycle && (
            <TextField label="Δ.Ο.Υ." name="wastereceptionpointCompanyTaxOffice" />
          )}
        </FormRow>
        <FormRow>
          <FieldInput
            disabled={!canEditSection3b}
            as={FormCol}
            label="Βάρος Φορτίου (κιλά)"
            name="receptionNetWeight"
            type="number"
            required={values?.isCancelled ? false : hasDateStorage(values?.dateStorage)}
          />
          <FieldInput
            disabled={!canEditSection3b}
            as={FormCol}
            label="Αριθμός Δελτίου Ζύγισης"
            name="wastereceptionpointNoteDocNumber"
            // type="number"
            // required={!values?.isCancelled}
          />
        </FormRow>
        <FormRow>
          <FieldInput
            disabled={!canEditSection3b}
            as={FormCol}
            label="Υπεύθυνος"
            name="receptionPersonName"
          />
          <FieldInput
            disabled={!canEditSection1}
            as={FormCol}
            label="Email"
            name="wastereceptionpointCompanyEmail"
          />
        </FormRow>

        {/* Έντυπο συνδεδεμένο με Εσωτερική Διακίνηση (μόνο προβολή) */}
        {values?.transferredWithITransId && (
          <div
            className="mb-4 mt-2 rounded border overflow-hidden"
            style={{ borderColor: '#b5c7d6' }}
          >
            <div
              className="form-group-title-alt mb-0"
              style={{ marginTop: 0, marginBottom: 0, borderRadius: 0 }}
            >
              <h6 className="mb-0">{t('Εσωτερική Διακίνηση Αποβλήτων')}</h6>
              <div className="text-white-50 d-block mb-0.5 mt-0.5">
                {t(
                  'Τα απόβλητα του εν λόγω εντύπου μεταφέρθηκαν αλλού, σύμφωνα με την ακόλουθη Εσωτερική Διακίνηση',
                )}
              </div>
            </div>
            <div className="px-3 pb-1 pt-2" style={{ backgroundColor: '#f0f4f8' }}>
              <FormRow>
                <FormCol>
                  <div className="mb-2">
                    <label className="form-label text-muted mb-1">{t('Κωδικός Διακίνησης')}</label>
                    <div className="fw-semibold">
                      <Link
                        to={generatePath(getRouteByName('ITran'), {
                          id: values.transferredWithITransId,
                        })}
                      >
                        {transferredITrans?.code ?? values.transferredWithITransId}
                      </Link>
                    </div>
                  </div>
                </FormCol>
                <FormCol>
                  <div className="mb-2">
                    <label className="form-label text-muted mb-1">
                      {t('Ημερομηνία Διακίνησης')}
                    </label>
                    <div>
                      {FormatDateTime(transferredITrans?.transactionDate, 'dd/MM/yyyy HH:mm')}
                    </div>
                  </div>
                </FormCol>
                <FormCol>
                  <div className="mb-2">
                    <label className="form-label text-muted mb-1">{t('Προέλευση')}</label>
                    <div>{transferredITransOriginName ?? '-'}</div>
                  </div>
                </FormCol>
                <FormCol>
                  <div className="mb-2">
                    <label className="form-label text-muted mb-1">{t('Προορισμός')}</label>
                    <div>{transferredITrans?.toWastereceptionpoint?.name ?? '-'}</div>
                  </div>
                </FormCol>
              </FormRow>
            </div>
          </div>
        )}

        {/*  --------------------------------------------------------------------------------------   */}
        {/*  4. Τελικός Αποδέκτης   ---------------------------------------------------------------   */}
        {/*  --------------------------------------------------------------------------------------   */}
        {(isMedicycle || isRebatteryLiion) && (
          <>
            <h5 className="form-group-title">4. {t('Τελικός Αποδέκτης')}</h5>
            <FormRow>
              <FieldSelectWasteReceptionPoint
                name="finaldestinationpointId"
                required={
                  values?.isCancelled ? false : isFieldRequired('finaldestinationpointId', values)
                }
                disabled={!canEditSection3a}
                as={FormCol}
                defaultValues={data}
                onChange={onFinaldestinationpointChange}
              />
              <FieldDatepicker
                name="finalDestinationDate"
                required={
                  values?.isCancelled ? false : isFieldRequired('finalDestinationDate', values)
                }
                disabled={!canEditSection3b}
                as={FormCol}
                label="Ημ/νία & Ώρα Παράδοσης στον Τελικό Αποδέκτη"
                hasMask={true}
                placeholder={'dd/mm/yyyy HH:mm'}
                showTimeSelect
                dateFormat="d/M/yyyy HH:mm"
                timeFormat="HH:mm"
                timeIntervals={15}
              />
              <FieldInput
                name="finalDestinationITrans.code"
                disabled={true}
                as={FormCol}
                label={
                  <Link to={`/itran/${values.finalDestinationItransId}`}>{t('Διακίνηση')}</Link>
                }
              />
            </FormRow>

            <FormRow>
              <FieldSelectWasteDestinationType
                name="finaldestinationpoint.wasteDestinationTypeId"
                required={false}
                disabled={true}
                as={FormCol}
                label={t('Κατηγορία Τελικού Αποδέκτη')}
                defaultValues={data}
              />
              <TextField label="Διεύθυνση" name="finaldestinationCompanyAddress" />
              <TextField label="ΑΜ. ΗΜΑ" name="finaldestinationCompanyImaCode" />
            </FormRow>

            <FormRow>
              <FieldInput
                name="finaldestinationCompanyPhoneNumber"
                required={
                  values?.isCancelled
                    ? false
                    : isFieldRequired('finaldestinationCompanyPhoneNumber', values)
                }
                disabled={!canEditSection3a}
                as={FormCol}
                label="Τηλέφωνο"
              />
              <FieldSelectRegion
                name={'finaldestinationCompanyRegionId'}
                required={
                  values?.isCancelled
                    ? false
                    : isFieldRequired('finaldestinationCompanyRegionId', values)
                }
                as={FormCol}
              />
              <TextField label={CompanyVatTitle} name="finaldestinationCompanyVat" />
              {!isAfisCyprus && !isMedicycle && (
                <TextField label="Δ.Ο.Υ." name="finaldestinationCompanyTaxOffice" />
              )}
            </FormRow>
            <FormRow>
              <FieldInput
                name="finalDestinationNetWeight"
                required={
                  values?.isCancelled ? false : isFieldRequired('finalDestinationNetWeight', values)
                }
                disabled={!canEditSection3b}
                as={FormCol}
                label="Βάρος Τελικού Αποδέκτη (κιλά)"
                type="number"
              />
              <FieldInput
                name="finaldestinationCompanyNoteDocNumber"
                disabled={!canEditSection1}
                as={FormCol}
                label="Αριθμός Δελτίου Ζύγισης"
              />
            </FormRow>

            <FormRow>
              <FieldInput
                name="finalDestinationPersonName"
                disabled={!canEditSection3b}
                as={FormCol}
                label="Υπεύθυνος"
              />
              <FieldInput
                name="finaldestinationCompanyEmail"
                disabled={!canEditSection1}
                as={FormCol}
                label="Email"
              />
            </FormRow>

            <FormRow>
              <FormCol></FormCol>
              <FileRepoImage fileId={data?.finalDestinationSignFileId} />
            </FormRow>
            <FormRow>
              <FormCol></FormCol>
              <FileRepoImage fileId={data?.receptionSignFileId} />
            </FormRow>
          </>
        )}
        {/* {data && ObjectFields(data)} */}
        {/* {errors && !isValid && JSON.stringify(errors)} */}
      </div>
    </>
  );
};
